import { IconButton } from "@mui/material"
import { prevent } from "lib/prevent"
import { indexAuthoringTasksInSchedule } from "./lib/index-authoring-tasks-in-schedule"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { useScheduleEditorIds } from "./hooks/use-schedule-editor-ids"
import { useScheduleEditorActions } from "./hooks/use-schedule-editor-actions"
import Iconify from "minimals-template/components/Iconify"
import { infoSnackbar } from "lib/snackbar/info-snackbar"
import { errorSnackbar } from "lib/snackbar/error-snackbar"
import { useDialog } from "lib/@hooks/useDialog"
import { deleteTaskWithAmends } from "./controller/delete-task-with-amends"
import { DeleteTaskAmendsDialog } from "./delete-task-amends-dialog"
import {
    SCHEDULE_EDITOR_ACTION_DELETE_TASK,
    SCHEDULE_EDITOR_ACTION_RESTORE_DELETED_TASK_WITH_AMENDS,
} from "./lib/schedule-editor-action-types"

export function AuthoringDeleteTaskButton() {
    const { schedule, rawSchedule, task, amendsContext, getTaskAmendsKey, macroMap } = useBoundContext()
    const { projectId, scheduleId } = useScheduleEditorIds()
    const { pushUndo, notifyDraftStructureChanged, refreshAmends, flushDraftBeforeSave } = useScheduleEditorActions()
    const openDeleteTaskAmendsDialog = useDialog(<DeleteTaskAmendsDialog />, { maxWidth: "md", fullWidth: true })

    return (
        <IconButton
            aria-label="Delete task"
            color="secondary"
            onClick={prevent(deleteTask)}
            size="small"
            data-cy="remove-button"
        >
            <Iconify icon="mdi:delete" />
        </IconButton>
    )

    async function deleteTask() {
        const { canDelete, amendsKey, noteData } = await getDeleteTaskAmendsInput()
        if (!canDelete) return

        const taskTitle = task?.title
        const taskIdentifier = task?.headerTaskId ?? task?.taskId ?? task?.id
        const removedTask = { ...task }
        const insertIndex = task.$sort
        const restoreActionType = canDeleteWithAmends({ amendsKey, taskId: taskIdentifier })
            ? SCHEDULE_EDITOR_ACTION_RESTORE_DELETED_TASK_WITH_AMENDS
            : SCHEDULE_EDITOR_ACTION_DELETE_TASK

        const deleteHandled = await handleDeletedTaskAfterRemoval({ amendsKey, noteData, taskId: taskIdentifier })
        if (!deleteHandled) return

        schedule.tasks.splice(insertIndex, 1)
        indexAuthoringTasksInSchedule(schedule)
        notifyDraftStructureChanged?.()
        infoSnackbar(`Task "${taskTitle}" deleted`)

        if (pushUndo) {
            pushUndo({
                type: restoreActionType,
                task: removedTask,
                index: insertIndex,
                amendsKey: canDeleteWithAmends({ amendsKey, taskId: taskIdentifier }) ? amendsKey : undefined,
            })
        }
    }

    async function getDeleteTaskAmendsInput() {
        if (!amendsContext || !getTaskAmendsKey) return { canDelete: true }

        const amendsKey = getTaskAmendsKey(task)
        if (!amendsKey) return { canDelete: true }

        const amendsState = amendsContext.getAmendsState(amendsKey)
        const result = await openDeleteTaskAmendsDialog({
            taskTitle: task?.title,
            initialNoteData: amendsState?.savedNoteData,
            macroMap,
        })
        if (!result) return { canDelete: false }

        return {
            canDelete: true,
            amendsKey,
            noteData: result.noteData,
        }
    }

    async function handleDeletedTaskAfterRemoval({ amendsKey, noteData, taskId }) {
        if (canDeleteWithAmends({ amendsKey, taskId })) {
            try {
                if (flushDraftBeforeSave) {
                    const flushed = await flushDraftBeforeSave()
                    if (flushed === false) return false
                }
                const deleted = await deleteTaskWithAmends({
                    projectId,
                    scheduleId,
                    taskId,
                    amends: { ...noteData, amendsKey },
                })
                if (deleted) rememberDeletedTask()
                if (deleted && refreshAmends) {
                    await refreshAmends()
                }
                return Boolean(deleted)
            } catch (error) {
                console.error("Failed to delete task with author notes", error)
                errorSnackbar("Failed to save deleted task author notes")
                return false
            }
        }

        return true
    }

    function rememberDeletedTask() {
        if (!rawSchedule) return
        rawSchedule.currentlyDeletedTasks = [...(rawSchedule.currentlyDeletedTasks ?? []), { ...task }]
    }

    function canDeleteWithAmends({ amendsKey, taskId }) {
        return Boolean(projectId && scheduleId && amendsKey && taskId)
    }
}
